import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
  ViewEncapsulation,
  WritableSignal,
  computed
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  CardContainerComponent,
  ChevronPosition,
  closePopupData,
  ControlRegexSignalDirective,
  createErrorPopupData,
  CustomLineSkeletonComponent,
  EmailSignalInputComponent,
  emailValidate,
  EnumWidth,
  IMPORTED_REGISTRATION_NUMBER_INPUT_REGEX,
  InfobubbleComponent,
  InformationCardComponent,
  LiveControlRegexSignalDirective,
  LoadingButtonComponent,
  MobilePhoneSignalInputComponent,
  mobilePhoneValidate,
  NON_IMPORTED_REGISTRATION_NUMBER_INPUT_REGEX,
  requiredYesNo,
  StatusMessagePopupV2Component,
  TextSignalInputComponent,
  TRANSLATE_SERVICE_TOKEN,
  TranslateInterface,
  VIN_NUMBER_REGEX,
  VIN_NUMBER_REGEX_INPUT,
  YesNoSignalInputComponent,
  YesNoTranslation
} from '@tribeauto/ngx-mobilite-material';
import { DEFAULT_DEMAT_DELIVERY, DematDeliveryData } from './demat-delivery.component.model';
import { catchError, EMPTY, filter, finalize, map, Observable, of, startWith, tap } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ApplicationStore } from '../../../../../core/services/application-store.service';
import { VehicleCategory } from '../../../../../core/services/application.model';
import { AsyncPipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DeliveryService } from '../delivery.service';
import { dematDeliveryDataToDeliveryRequest } from '../delivery.component.mapper';
import { EsivService } from '../esiv.service';
import { mapToSivSearch } from '../esiv.mapper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  debounce,
  disabled,
  form,
  FormField,
  hidden,
  maxLength,
  minLength,
  required,
  validate
} from '@angular/forms/signals';
import { AUTO_CLOSE_DELAY_TEN_SECONDS } from '../../../../helpers/popup.helper';
import { SignatureEnum } from '../../contract-response.model';

export const vehicleCategoriesEligibleToRegistration = [
  VehicleCategory.CAR,
  VehicleCategory.MOTOR_BIKE,
  VehicleCategory.CAMPER,
  VehicleCategory.MOBILE_HOME
];

@Component({
  selector: 'pvmo-demat-delivery',
  imports: [
    CardContainerComponent,
    InformationCardComponent,
    LoadingButtonComponent,
    ReactiveFormsModule,
    AsyncPipe,

    CustomLineSkeletonComponent,
    InfobubbleComponent,
    TranslatePipe,
    StatusMessagePopupV2Component,
    TextSignalInputComponent,
    FormField,
    YesNoSignalInputComponent,
    EmailSignalInputComponent,
    MobilePhoneSignalInputComponent,
    LiveControlRegexSignalDirective,
    ControlRegexSignalDirective
  ],
  providers: [
    {
      provide: TRANSLATE_SERVICE_TOKEN,
      useFactory: (translateService: TranslateService): TranslateInterface => ({
        get: (key: string): string => translateService.instant(key)
      }),
      deps: [TranslateService]
    }
  ],
  templateUrl: './demat-delivery.component.html',
  styleUrl: './demat-delivery.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class DematDeliveryComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly deliveryService = inject(DeliveryService);
  private readonly esivService = inject(EsivService);
  private readonly cdr = inject(ChangeDetectorRef);

  registrationNumberHint: WritableSignal<string> = signal('');

  isVadDelivery = computed(() => {
    const signatureType = this.applicationStore.instant('signatureData')()?.signatureType;
    return signatureType != null && [SignatureEnum.VAD_DELIVERY, SignatureEnum.VAD].includes(signatureType);
  });

  deliveryModeTranslations$: Observable<YesNoTranslation> = this.translateService.onTranslationChange.pipe(
    startWith(''),
    map(() => ({
      yes: this.translateService.instant('application.demat-delivery.delivery-mode.home'),
      no: this.translateService.instant('application.demat-delivery.delivery-mode.concession')
    }))
  );
  dematDeliveryContract = this.applicationStore.instant('dematDeliveryContractData');
  statusPopupElement = closePopupData(createErrorPopupData('', '', '', undefined));

  isVehicleImported = this.dematDeliveryContract()?.import;
  immatRegex = this.isVehicleImported
    ? IMPORTED_REGISTRATION_NUMBER_INPUT_REGEX
    : NON_IMPORTED_REGISTRATION_NUMBER_INPUT_REGEX;
  vinRegex = VIN_NUMBER_REGEX_INPUT;

  isFetchVehicleLoading = signal(false);
  isDeliveryLoading = signal(false);

  readonly dematDelivery = signal<DematDeliveryData>({
    ...DEFAULT_DEMAT_DELIVERY,
    registrationNumber: this.dematDeliveryContract()?.registrationNumber ?? '',
    emailAddress: this.dematDeliveryContract()?.emailAddress ?? '',
    mobilePhoneNumber: this.dematDeliveryContract()?.mobilePhoneNumber ?? ''
  });
  readonly dematDeliveryForm = form(this.dematDelivery, path => {
    requiredYesNo(path.distanceSignature);
    hidden(path.registrationNumber, { when: () => !this.isVehicleCategoryEligibleToRegistration() });
    required(path.registrationNumber, {
      when: () => this.dematDeliveryContract()?.usedVehicle ?? false
    });
    debounce(path.registrationNumber, 500);
    required(path.serialNumber);
    maxLength(path.serialNumber, 17);
    minLength(path.serialNumber, 17);
    validate(path.serialNumber, ({ value }) =>
      !value() || VIN_NUMBER_REGEX.test(value()) ? undefined : { kind: 'pattern' }
    );
    disabled(path.serialNumber, {
      when: ({ valueOf }) => !!valueOf(path.registrationNumber) && !this.registrationNumberHint()
    });
    required(path.emailAddress);
    emailValidate(path.emailAddress);
    required(path.mobilePhoneNumber);
    mobilePhoneValidate(path.mobilePhoneNumber);
  });
  protected readonly enumWidth = EnumWidth;
  protected readonly chevronPosition = ChevronPosition;

  constructor() {
    effect(() => {
      const registrationNumber = this.dematDeliveryForm.registrationNumber().value();
      untracked(() => {
        this.registrationNumberHint.set('');
        if (this.dematDeliveryForm.registrationNumber().valid()) {
          this.fetchVehicle(registrationNumber.toUpperCase());
        }
        if (!registrationNumber) {
          this.dematDeliveryForm.serialNumber().value.set('');
        }
      });
    });
  }

  ngOnInit(): void {
    if (this.isVehicleCategoryEligibleToRegistration()) {
      const registrationNumberFromContract = this.dematDeliveryContract()?.registrationNumber;
      if (registrationNumberFromContract) {
        this.fetchVehicle(registrationNumberFromContract.toUpperCase());
      }
    }
  }

  submit(): void {
    const externalId = this.activatedRoute.parent?.snapshot.paramMap.get('id');
    const webstoreId = this.applicationStore.instant('webstoreData')()?.webstoreId;
    if (externalId && webstoreId) {
      this.isDeliveryLoading.set(true);
      const formValue = { ...this.dematDelivery() };
      if (this.isVadDelivery()) {
        formValue.distanceSignature = true;
      }
      this.deliveryService
        .getDeliveryDocESignatureURL(webstoreId, externalId, dematDeliveryDataToDeliveryRequest(formValue))
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.setErrorStatusPopup();
            return of(null);
          }),
          finalize(() => {
            this.isDeliveryLoading.set(false);
          })
        )
        .subscribe(deliveryResponse => {
          window.open(deliveryResponse?.esignUrl, '_blank');
        });
    } else {
      this.setErrorStatusPopup();
    }
  }

  closeStatusMessagePopup(): void {
    this.statusPopupElement = closePopupData(this.statusPopupElement);
  }

  fetchVehicle(registrationNumber: string): void {
    this.isFetchVehicleLoading.set(true);
    this.esivService
      .fetchVehicle(mapToSivSearch(registrationNumber))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(response => !!response),
        tap(response => {
          const garageResponseFromResponse = response[registrationNumber];
          if (garageResponseFromResponse && garageResponseFromResponse.immatSiv === registrationNumber) {
            this.dematDeliveryForm.registrationNumber().value.set(response[registrationNumber].immatSiv); // emitevent false
            this.dematDeliveryForm.serialNumber().value.set(response[garageResponseFromResponse.immatSiv].vinNumber); // emitevent false
          } else {
            this.setRegistrationNumberAndSerialNumberValuesWhenError();
          }
          this.isFetchVehicleLoading.set(false);
          this.cdr.markForCheck();
        }),
        catchError(() => {
          this.isFetchVehicleLoading.set(false);
          this.setRegistrationNumberAndSerialNumberValuesWhenError();
          return EMPTY;
        })
      )
      .subscribe();
  }

  private isVehicleCategoryEligibleToRegistration(): boolean {
    return (
      !!this.dematDeliveryContract() &&
      vehicleCategoriesEligibleToRegistration.includes(
        this.dematDeliveryContract()?.vehicleCategory ?? VehicleCategory.OTHER
      )
    );
  }

  private setRegistrationNumberAndSerialNumberValuesWhenError(): void {
    this.registrationNumberHint.set('hint.vehicle-not-found');
    this.dematDeliveryForm.serialNumber().value.set('');
  }

  private setErrorStatusPopup(): void {
    this.statusPopupElement = createErrorPopupData(
      this.translateService.instant('application.demat-delivery.error.title'),
      this.translateService.instant('application.demat-delivery.error.message'),
      this.translateService.instant('application.demat-delivery.error.close-label'),
      AUTO_CLOSE_DELAY_TEN_SECONDS
    );
    this.cdr.markForCheck();
  }
}
